import Phaser from "phaser";
import { HealthBar } from "../ui/health-bar";
import { ReloadTimerBar } from "../ui/reload-timer-bar";
import { Enemy } from "../models/enemy";
import { playSound } from "../utils/sound-utils";
import { EnemyState, TILE_SIZE } from "../models/common";
import { showSplashParticle } from "../ui/splash-particle";
import { spawnBloodSplatter } from "./attack-handler";

export type EnemyRender = {
  sprite: Phaser.GameObjects.Sprite;
  weaponSprite?: Phaser.GameObjects.Sprite; // Add weapon sprite
  hatSprite?: Phaser.GameObjects.Sprite; // Bot's randomly assigned hat, if any
  container?: Phaser.GameObjects.Container;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  lastUpdate: number;
  aggro: boolean;
  aggroText?: Phaser.GameObjects.Text;
  health: number;
  maxHealth: number;
  healthbar?: HealthBar;
  scale?: number; // Enemy size scale factor
  reloadTimerBar?: ReloadTimerBar; // Add reload timer bar
  weapon?: any; // Weapon data from server
  hitboxGraphics?: Phaser.GameObjects.Graphics; // For hitbox debugging
  spriteSize?: number; // Sprite frame size in pixels
  behaviorText?: Phaser.GameObjects.Text; // Debug: behavior name display
  setMoving?: (moving: boolean) => void; // Toggles the body/hat walk hop
  lastMovedAt?: number; // When this enemy last actually moved (client clock)
};

export class EnemyManager {
  // Helper function to get texture key for enemy sprite
  static getEnemyTextureKey(spritePath: string): string {
    // Map common sprite paths to preloaded texture keys
    const spriteMap: Record<string, string> = {
      "enemies/bot.png": "enemy_bot",
      "enemies/creepy.png": "enemy_creepy",
      "enemies/goo-boom.png": "enemy_goboom",
      "enemies/mutant-lord.png": "enemy_mutant_lord",
      "enemies/scavenger.png": "enemy_scavenger",
    };
    const textureKey = spriteMap[spritePath];
    if (!textureKey) {
      console.warn(
        `Unknown enemy sprite path: ${spritePath}, falling back to creepy`,
      );
      return "enemy_creepy"; // fallback to creepy
    }
    return textureKey;
  }

  enemies: Record<string, EnemyRender> = {};
  scene: Phaser.Scene;
  private hitboxDebugGraphics: Phaser.GameObjects.Graphics | null = null; // Batched graphics for hitbox debug

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  updateEnemies(serverEnemies: EnemyState[]) {
    // Remove enemies not present in server update or marked dead
    const serverIds = new Set(
      serverEnemies.filter((e) => !e.dead).map((e) => e.id),
    );
    Object.keys(this.enemies).forEach((id) => {
      // Remove if not present or marked dead
      const serverEnemy = serverEnemies.find((e) => e.id === id);
      if (!serverIds.has(id) || (serverEnemy && serverEnemy.dead)) {
        const enemy = this.enemies[id];
        const px = enemy.container ? enemy.container.x : enemy.sprite.x;
        const py = enemy.container ? enemy.container.y : enemy.sprite.y;
        // Show blood splatter if not already done
        if (enemy.sprite && !enemy.sprite.getData("splatter")) {
          enemy.sprite.setData("splatter", true);

          spawnBloodSplatter(
            this.scene,
            px,
            py,
            (this.scene as any).bloodSplatters,
          );
        }
        // Stop the walk hop first: its tween targets a plain object, so destroying the
        // container alone would leave it running.
        enemy.setMoving?.(false);
        // Remove container (destroys all children: sprite, healthbar, aggroText, etc.)
        if (enemy.container) {
          enemy.container.destroy();
        }

        // Destroy reload timer bar if it exists
        if (enemy.reloadTimerBar) {
          enemy.reloadTimerBar.destroy();
          enemy.reloadTimerBar = undefined;
        }

        // Show splash particle
        if (this.scene) {
          showSplashParticle(this.scene, px, py);
        }
        // Finally, delete reference
        delete this.enemies[id];
      }
    });
    // Add/update current enemies (skip dead)
    serverEnemies
      .filter((e) => !e.dead)
      .forEach((e) => {
        const showAggro = e.showAggro || false;
        // Use direct number values for position
        const x = e.x;
        const y = e.y;
        // Prevent movement if blocked by tile-objects-block
        // if (mapHandler && mapHandler.isBlocked && mapHandler.isBlocked(x, y)) {
        //     return; // Skip updating enemy if blocked
        // }
        if (!this.enemies[e.id]) {
          // For bots with a skin, use the skin texture; otherwise use the default enemy texture
          const isBot = e.id.startsWith("bot_");
          let textureKey: string;
          if (isBot && e.skin && this.scene.textures.exists(e.skin)) {
            textureKey = e.skin;
          } else {
            textureKey = EnemyManager.getEnemyTextureKey(e.definition.sprite);
          }

          // Verify the texture exists before creating the enemy
          if (!this.scene.textures.exists(textureKey)) {
            console.error(
              `Enemy texture '${textureKey}' not found for sprite '${e.definition.sprite}'. Enemy will not be created.`,
            );
            return;
          }

          // Only show aggro symbol for non-bot enemies
          const shouldShowAggro = showAggro && !isBot;

          // Use Enemy.createContainer from the model
          const {
            container,
            sprite,
            healthbar,
            aggroText,
            weaponSprite,
            hatSprite,
            behaviorText,
            setMoving,
          } = Enemy.createContainer({
            scene: this.scene,
            x,
            y,
            id: e.id,
            name: e.name,
            texture: textureKey,
            health: e.health ?? 100,
            maxHealth: e.definition.health ?? 100,
            showAggro: shouldShowAggro,
            scale: e.definition.size || 1,
            spriteSize: e.definition.spriteSize || 32,
            weapon: e.weapon, // Pass weapon data
            hat: isBot ? e.hat : undefined,
          });

          // Make sure UI camera ignores this enemy container if it exists
          if ((this.scene as any).uiCamera && container) {
            (this.scene as any).uiCamera.ignore(container);
          }

          // Ensure health is set to the server value so first hit is detected
          // Initialize both current and target positions to server position
          // This prevents interpolation issues on the first frame
          const enemy = {
            sprite,
            weaponSprite, // Store weapon sprite
            hatSprite, // Store hat sprite
            container,
            x,
            y,
            targetX: x,
            targetY: y,
            lastUpdate: Date.now(), // Use client timestamp to avoid clock sync issues
            aggro: showAggro,
            aggroText,
            behaviorText, // Store behavior text for updates
            health: e.health, // always use server value
            maxHealth: e.definition.health ?? 100,
            healthbar,
            scale: e.definition.size || 1, // Store scale for weapon rotation
            reloadTimerBar: undefined as ReloadTimerBar | undefined, // Initialize reload timer bar
            weapon: e.weapon, // Store weapon data for sound effects
            spriteSize: e.definition.spriteSize || 32, // Store sprite size for hitbox calculation
            hitboxGraphics: undefined as
              | Phaser.GameObjects.Graphics
              | undefined, // For hitbox debugging
            setMoving, // Toggles the body/hat walk hop
            lastMovedAt: 0,
          };

          // Immediately update container position to match server state
          // This prevents the enemy from being at (0,0) on first frame
          if (container) {
            container.setPosition(x, y);
            // Update depth based on Y position for proper rendering order
            // Use 1000 + y to keep above static tile layers (0-2) but allow Y-sorting
            container.setDepth(1000 + y);
          }

          this.enemies[e.id] = enemy;
        } else {
          const enemy = this.enemies[e.id];
          const newHealth = e.health ?? 100;
          // If health is undefined, initialize it (first update after creation)
          if (typeof enemy.health === "undefined") {
            enemy.health = newHealth;
          }
          const prevHealth = enemy.health;
          if (newHealth < prevHealth) {
            // Spawn blood splatter at enemy position
            if (this.scene && (this.scene as any).bloodSplatters) {
              // Random direction for splatter
              const angle = Math.random() * Math.PI * 2;
              const dx = Math.cos(angle) * 10;
              const dy = Math.sin(angle) * 10;
              const px = enemy.container ? enemy.container.x : enemy.sprite.x;
              const py = enemy.container ? enemy.container.y : enemy.sprite.y;
              spawnBloodSplatter(
                this.scene,
                px,
                py,
                (this.scene as any).bloodSplatters,
              );
            }
          }

          enemy.targetX = e.x;
          enemy.targetY = e.y;
          enemy.lastUpdate = Date.now(); // Use client timestamp to avoid clock sync issues

          const enemyTileX = Math.floor(enemy.x / TILE_SIZE);
          const enemyTileY = Math.floor(enemy.y / TILE_SIZE);

          // Detect aggro state change and play SFX
          const wasAggro = enemy.aggro;
          const isNowAggro = showAggro;
          if (!wasAggro && isNowAggro && e.definition.sfx) {
            // Enemy just turned aggro - play the aggro sound
            const soundKey = `enemy-${e.definition.sfx.replace(".ogg", "")}`;
            if (this.scene.sound && this.scene.cache.audio.exists(soundKey)) {
              playSound(this.scene, soundKey, {
                tileX: enemyTileX,
                tileY: enemyTileY,
              });
            }
          }

          // Only show aggro symbol for non-bot enemies
          const isBot = e.id.startsWith("bot_");
          if (showAggro && !isBot && !enemy.aggroText) {
            // Calculate aggro position based on sprite size
            const spriteSize = e.definition.spriteSize || 32;
            const scale = enemy.scale || 1;
            const effectiveScale = (spriteSize / 32) * scale;
            const aggroYOffset = -32 * effectiveScale;

            // Add aggro text to container if not already present
            const aggroText = this.scene.add
              .text(0, aggroYOffset, "!", { font: "24px Anton", color: "#ff0" })
              .setOrigin(0.5);
            if (enemy.container) enemy.container.add(aggroText);
            enemy.aggroText = aggroText;
          } else if ((!showAggro || isBot) && enemy.aggroText) {
            enemy.aggroText.destroy();
            enemy.aggroText = undefined;
          }
          enemy.aggro = showAggro;

          // Update behavior text display (debug feature)
          if (enemy.behaviorText) {
            const behaviorName = e.activeBehaviorName || "";
            enemy.behaviorText.setText(
              behaviorName + "\n" + enemyTileX + ", " + enemyTileY,
            );
          }

          enemy.health = newHealth;
          enemy.maxHealth = e.definition.health ?? 100;
          enemy.spriteSize = e.definition.spriteSize || 32;
          if (enemy.healthbar) {
            enemy.healthbar.setHealth(newHealth);
          }

          // Handle reload timer for bosses
          const isBoss = e.id.startsWith("boss_");
          if (isBoss && e.reloading) {
            // Create reload timer bar if it doesn't exist
            if (!enemy.reloadTimerBar) {
              const scale = enemy.scale || 1;
              // Play reload sound
              playSound(this.scene, "weapon-reload", {
                tileX: enemyTileX,
                tileY: enemyTileY,
              });

              enemy.reloadTimerBar = new ReloadTimerBar(
                this.scene,
                -20,
                -48 * scale,
                40,
                8,
              );
              if (enemy.container) {
                enemy.container.add(enemy.reloadTimerBar.bar);
                enemy.container.add(enemy.reloadTimerBar.text);
              }
            }
            // Update reload progress
            if (enemy.reloadTimerBar && e.reloadProgress !== undefined) {
              enemy.reloadTimerBar.setProgress(e.reloadProgress);
              enemy.reloadTimerBar.show();
            }
          } else if (enemy.reloadTimerBar) {
            // Hide and destroy reload timer bar when not reloading
            enemy.reloadTimerBar.hide();
            if (!e.reloading) {
              enemy.reloadTimerBar.destroy();
              enemy.reloadTimerBar = undefined;
            }
          }
        }
      });
  }

  interpolateEnemies() {
    const now = Date.now();
    const TILE_SIZE = 32;
    // Below this per-frame travel an enemy counts as standing still, and it has to hold
    // still this long before the walk hop stops - otherwise the hop flickers off in the
    // gap between reaching a target and the next server update arriving.
    const MOVE_EPSILON = 0.05;
    const MOVE_LINGER_MS = 250;
    Object.values(this.enemies).forEach((enemy) => {
      // Store old tile position before interpolation
      const oldTileX = Math.floor(enemy.x / TILE_SIZE);
      const oldTileY = Math.floor(enemy.y / TILE_SIZE);

      const interpTime = 700; // was 100
      const elapsed = Math.min(interpTime, now - enemy.lastUpdate);
      const t = elapsed / interpTime;

      // Calculate differences
      const diffX = Math.abs(enemy.x - enemy.targetX);
      const diffY = Math.abs(enemy.y - enemy.targetY);

      const jumpThreshold = 64; // pixels

      // If the difference is larger than 48, move immediately
      if (diffX > jumpThreshold || diffY > jumpThreshold) {
        enemy.x = enemy.targetX;
        enemy.y = enemy.targetY;
        // A teleport-sized correction isn't walking, so leave the hop as it was.
        if (enemy.container) {
          enemy.container.setPosition(enemy.targetX, enemy.targetY);
          // Update depth based on Y position for proper rendering order
          // Use 1000 + y to keep above static tile layers (0-2) but allow Y-sorting
          enemy.container.setDepth(1000 + enemy.targetY);
        } else {
          enemy.sprite.setPosition(enemy.targetX, enemy.targetY);
        }
        return;
      }

      // Interpolate positions
      const newX = Phaser.Math.Interpolation.Linear(
        [enemy.x, enemy.targetX],
        t,
      );
      const newY = Phaser.Math.Interpolation.Linear(
        [enemy.y, enemy.targetY],
        t,
      );

      // Flip sprite based on target direction (not interpolation direction)
      if (enemy.targetX < enemy.x) {
        enemy.sprite.setFlipX(true);
        enemy.hatSprite?.setFlipX(true);
      } else if (enemy.targetX > enemy.x) {
        enemy.sprite.setFlipX(false);
        enemy.hatSprite?.setFlipX(false);
      }

      if (enemy.container) {
        enemy.container.setPosition(newX, newY);
        // Update depth based on Y position for proper rendering order
        // Use 1000 + y to keep above static tile layers (0-2) but allow Y-sorting
        enemy.container.setDepth(1000 + newY);
      } else {
        enemy.sprite.setPosition(newX, newY);
      }

      // Run the walk hop only while the enemy is actually covering ground.
      if (
        Math.abs(newX - enemy.x) > MOVE_EPSILON ||
        Math.abs(newY - enemy.y) > MOVE_EPSILON
      ) {
        enemy.lastMovedAt = now;
      }
      enemy.setMoving?.(now - (enemy.lastMovedAt ?? 0) < MOVE_LINGER_MS);

      // Update enemy's current position
      enemy.x = newX;
      enemy.y = newY;
    });
  }

  destroyAll() {
    Object.values(this.enemies).forEach((enemy) => {
      enemy.setMoving?.(false);
      enemy.container?.destroy();
      enemy.hitboxGraphics?.destroy();
    });
    this.enemies = {};
  }

  /**
   * Show or hide debug AI state text for all enemies based on admin mode.
   * Also displays the enemy's x,y tile position below the AI state.
   */
  updateAllBehaviorText(adminMode: boolean) {
    Object.values(this.enemies).forEach((enemy) => {
      // SHow behavior text if in admin mode
      if (enemy.behaviorText) {
        if (adminMode) {
          enemy.behaviorText.setVisible(true);
        } else {
          enemy.behaviorText.setVisible(false);
        }
      }
    });
  }

  /**
   * Render green debug squares showing enemy hitboxes
   * Matches server-side collision detection
   * - hitbox radius = spriteSize / 2 (same as server's calculateHitboxRadius)
   * - vertical offset = -0.5 * spriteSize (matches server calculation)
   * Optimized: Uses single batched graphics object instead of per-enemy graphics
   */
  renderHitboxes() {
    // Only create graphics object if it doesn't exist
    if (!this.hitboxDebugGraphics) {
      this.hitboxDebugGraphics = this.scene.add.graphics();
      this.hitboxDebugGraphics.setDepth(1001);
    }

    const graphics = this.hitboxDebugGraphics;
    graphics.clear(); // Single clear for all hitboxes
    graphics.lineStyle(2, 0x00ff00, 1); // Single line style set

    // Batch render all enemy hitboxes
    Object.values(this.enemies).forEach((enemy) => {
      if (!enemy.container) return;

      const spriteSize = enemy.spriteSize || 32;
      const hitboxRadius = spriteSize / 2;

      const x = enemy.container.x;
      const y = enemy.container.y;

      const scale = enemy.scale || 1;
      const offsetY = -(spriteSize * scale - 32) / 2;
      const hitboxCenterX = x;
      const hitboxCenterY = y + offsetY;

      const rectWidth = hitboxRadius * 2;
      const rectHeight = hitboxRadius * 2;
      const rectX = hitboxCenterX - hitboxRadius;
      const rectY = hitboxCenterY - hitboxRadius;

      graphics.strokeRect(rectX, rectY, rectWidth, rectHeight);
      graphics.fillStyle(0x00ff00, 0.3);
      graphics.fillCircle(hitboxCenterX, hitboxCenterY, 2);
    });
  }

  /**
   * Clear all hitbox debug graphics
   */
  clearHitboxes() {
    Object.values(this.enemies).forEach((enemy) => {
      if (enemy.hitboxGraphics) {
        enemy.hitboxGraphics.clear();
      }
    });
  }

  /**
   * Cleanup all resources
   */
  cleanup() {
    if (this.hitboxDebugGraphics) {
      this.hitboxDebugGraphics.destroy();
      this.hitboxDebugGraphics = null;
    }
  }
}
